import React, { useCallback, useContext } from 'react'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { AsH3, ParagraphSM, TitleSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { ProjectMetaContext } from '@/contexts/ProjectMetaContext/ProjectMetaContext'
import { ProjectMeta } from '@/services/ProjectsService'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'

export const OverviewSection = () => {
  const track = useSafeTrack()
  const { t } = useTranslate('watch')
  const { project } = useContext(ProjectMetaContext)
  const description = getProjectDescription(project)
  const hubPageLink = getHubPageLink(project)

  const handleLearnMoreClick = useCallback(() => {
    track('Watch Project Learn More Clicked', { project: project?.slug })
  }, [project?.slug, track])

  return (
    <section id="overview" className="flex flex-col">
      <div className="flex flex-col gap-10 lg:flex-row lg:gap-14">
        {description && (
          <div className={classNames('basis-1/2', { 'basis-full': !project?.mission })}>
            <TitleSM weight="bold" className="md:photon-title-md lg:photon-title-lg mb-4" as={AsH3}>
              {t('showOverview', 'Show Overview')}
            </TitleSM>
            <ParagraphSM color="core-gray-500" className="md:photon-paragraph-md lg:photon-paragraph-lg">
              {description}
            </ParagraphSM>
          </div>
        )}
        {project?.mission && (
          <div className="basis-1/2">
            <TitleSM weight="bold" className="md:photon-title-md lg:photon-title-lg mb-4" as={AsH3}>
              {t('mission', 'Mission')}
            </TitleSM>
            <ParagraphSM color="core-gray-500" className="md:photon-paragraph-md lg:photon-paragraph-lg">
              {project.mission}
            </ParagraphSM>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 md:flex-row">
        <LinkButton href={hubPageLink} className="mt-10 w-fit" variant="white" outline onClick={handleLearnMoreClick}>
          {t('learnMore', 'Learn More')}
        </LinkButton>
      </div>
    </section>
  )
}

const getProjectDescription = (project: Maybe<ProjectMeta>): string => {
  if (!project) {
    return ''
  }

  if (project.title?.description) {
    if (project.title.description.long) {
      return project.title.description.long
    }

    if (project.title.description.medium) {
      return project.title.description.medium
    }

    if (project.title.description.short) {
      return project.title.description.short
    }
  }

  if (project.title) {
    if ('longDescription' in project.title && project.title.longDescription) {
      return String(project.title.longDescription)
    }
    if ('mediumDescription' in project.title && project.title.mediumDescription) {
      return String(project.title.mediumDescription)
    }
    if ('shortDescription' in project.title && project.title.shortDescription) {
      return String(project.title.shortDescription)
    }
  }

  if (project.theaterDescription) {
    return project.theaterDescription
  }

  return project.showOverview ?? ''
}

const getHubPageLink = (project: Maybe<ProjectMeta>): string => {
  if (!project) return '#'
  switch (project.projectType) {
    case 'special':
      return paths.shows.dryBar
    case 'series':
      return project.slug ? `${paths.shows.index}/${project.slug}` : paths.shows.index
    default:
      return project.slug ? `${paths.movies.index}/${project.slug}` : paths.movies.index
  }
}
